import { Injectable } from '@angular/core';

import {
  Dashboard,
  Widget,
  WidgetGroup,
  Comparison,
} from 'app/providers/bridge-api/dashboard.service';
import { Utils } from 'app/utils/utils';
import { SavedView } from '../models/saved-view.model';

export type WidgetChangeType =
  | 'none'
  | 'added'
  | 'deleted'
  | 'filter-overwrite'
  | 'chart-overwrite'
  | 'full-overwrite';

export interface SavedViewPropagationResult {
  hasChanges: boolean;
  widgetChanges: Map<string, WidgetChangeType>;
  addedWidgets: Widget[];
  deletedWidgetIds: string[];
  addedWidgetGroups: WidgetGroup[];
  deletedWidgetGroupIds: string[];
  addedComparisons: Comparison[];
  deletedComparisonIds: string[];
  addedPageIds: number[];
  deletedPageIds: number[];
}

const PRESERVED_FILTER_PROPERTIES = ['filters'] as const;

const PRESERVED_CHART_OPTIONS = [
  'graphType',
  'legends',
  'sort',
  'limit',
  'excludedValues',
  'chartMaximum',
  'chartMinimum',
  'chartInterval',
  'chartAxisValue',
  'chartSecondaryAxisValue',
  'chartSecondaryMaximum',
  'chartSecondaryMinimum',
  'chartSecondaryInterval',
  'chartGoal',
  'chartGoalDisplayAs',
  'chartGoalColor',
  'chartGoalShade',
  'displayChartValues',
  'colorByPoint',
  'donutOptions',
  'gaugeOptions',
  'dateMarkers',
  'showAsStacked',
  'display',
  'showWeekPrefix',
  'customItemColors',
  'breakdownOptions',
  'additionalBreakdownOptions',
] as const;

const DATA_SOURCE_PROPERTIES = ['dataSourceId'] as const;

const ANALYTIC_ITEM_PROPERTIES = [
  'analyticItem',
  'entityTypeFilter',
  'type',
  'aggregator',
] as const;

/**
 * Service responsible for propagating dashboard changes to saved views
 * according to the acceptance criteria rules.
 *
 * Rules Summary:
 * 1. NO CHANGES: Filter settings, chart options, custom sort, doc widget options,
 *    content limit, comparison on widget that already has one - NOT propagated.
 *
 * 2. NEW CONTENT ADDED: Comments, new widgets, widget groups, pages - added
 *    without overwriting existing saved view settings.
 *
 * 3. FILTER OPTIONS OVERWRITTEN: Widget added/removed from group, parent widget
 *    change - filters overwritten, chart options preserved.
 *
 * 4. CHART OPTIONS OVERWRITTEN: Analytic item changed - chart options overwritten,
 *    filters preserved.
 *
 * 5. ALL OPTIONS OVERWRITTEN: Data source changed, comparison added/removed on
 *    widget without one, widget/page deleted - full replacement.
 */
@Injectable({ providedIn: 'root' })
export class SavedViewPropagationService {
  /**
   * Analyzes changes between the current dashboard state and a saved view
   * to determine what propagation actions are needed.
   */
  analyzeChanges(dashboard: Dashboard, savedView: SavedView): SavedViewPropagationResult {
    const result: SavedViewPropagationResult = {
      hasChanges: false,
      widgetChanges: new Map(),
      addedWidgets: [],
      deletedWidgetIds: [],
      addedWidgetGroups: [],
      deletedWidgetGroupIds: [],
      addedComparisons: [],
      deletedComparisonIds: [],
      addedPageIds: [],
      deletedPageIds: [],
    };

    if (!dashboard || !savedView) {
      return result;
    }

    const dashboardWidgetIds = new Set(dashboard.widgets.map((w) => w.id));
    const savedViewWidgetIds = new Set((savedView.widgets ?? []).map((w) => w.id));

    for (const widget of dashboard.widgets) {
      if (!savedViewWidgetIds.has(widget.id)) {
        result.addedWidgets.push(widget);
        result.widgetChanges.set(widget.id, 'added');
        result.hasChanges = true;
      }
    }

    for (const savedWidget of savedView.widgets ?? []) {
      if (!dashboardWidgetIds.has(savedWidget.id)) {
        result.deletedWidgetIds.push(savedWidget.id);
        result.widgetChanges.set(savedWidget.id, 'deleted');
        result.hasChanges = true;
      }
    }

    for (const dashboardWidget of dashboard.widgets) {
      if (savedViewWidgetIds.has(dashboardWidget.id)) {
        const savedWidget = savedView.widgets?.find((w) => w.id === dashboardWidget.id);
        if (savedWidget) {
          const changeType = this.determineWidgetChangeType(
            dashboardWidget,
            savedWidget,
            dashboard,
            savedView
          );
          result.widgetChanges.set(dashboardWidget.id, changeType);
          if (changeType !== 'none') {
            result.hasChanges = true;
          }
        }
      }
    }

    this.analyzeWidgetGroupChanges(dashboard, savedView, result);
    this.analyzePageChanges(dashboard, savedView, result);

    return result;
  }

  /**
   * Determines the type of change for a specific widget.
   */
  private determineWidgetChangeType(
    dashboardWidget: Widget,
    savedWidget: Widget,
    dashboard: Dashboard,
    savedView: SavedView
  ): WidgetChangeType {
    if (this.hasDataSourceChanged(dashboardWidget, savedWidget)) {
      return 'full-overwrite';
    }

    const comparisonChange = this.getComparisonChangeType(dashboardWidget, savedWidget);
    if (comparisonChange === 'full-overwrite') {
      return 'full-overwrite';
    }

    const widgetGroupChange = this.getWidgetGroupChangeType(
      dashboardWidget,
      savedWidget,
      dashboard,
      savedView
    );
    if (widgetGroupChange === 'filter-overwrite') {
      return 'filter-overwrite';
    }

    if (this.hasAnalyticItemChanged(dashboardWidget, savedWidget)) {
      return 'chart-overwrite';
    }

    const dashboardPageIds = new Set(dashboard.payload.pages.map((p) => p.id));
    if (!dashboardPageIds.has(dashboardWidget.payload.page)) {
      return 'full-overwrite';
    }

    return 'none';
  }

  private hasDataSourceChanged(dashboardWidget: Widget, savedWidget: Widget): boolean {
    return dashboardWidget.dataSourceId !== savedWidget.dataSourceId;
  }

  private hasAnalyticItemChanged(dashboardWidget: Widget, savedWidget: Widget): boolean {
    for (const prop of ANALYTIC_ITEM_PROPERTIES) {
      if (dashboardWidget.payload[prop] !== savedWidget.payload[prop]) {
        return true;
      }
    }
    return false;
  }

  private getComparisonChangeType(
    dashboardWidget: Widget,
    savedWidget: Widget
  ): WidgetChangeType | 'none' {
    const dashboardComparisonId = dashboardWidget.payload.comparison;
    const savedComparisonId = savedWidget.payload.comparison;

    if (savedComparisonId) {
      return 'none';
    }

    if (dashboardComparisonId && !savedComparisonId) {
      return 'full-overwrite';
    }

    return 'none';
  }

  private getWidgetGroupChangeType(
    dashboardWidget: Widget,
    savedWidget: Widget,
    dashboard: Dashboard,
    savedView: SavedView
  ): WidgetChangeType | 'none' {
    const dashboardGroups = dashboard.widgetGroups ?? {};
    const savedViewGroups = savedView.widgetGroups ?? {};

    const dashboardGroupId = this.findWidgetGroupForWidget(dashboardWidget.id, dashboardGroups);
    const savedGroupId = this.findWidgetGroupForWidget(savedWidget.id, savedViewGroups);

    if (dashboardGroupId !== savedGroupId) {
      return 'filter-overwrite';
    }

    if (dashboardGroupId && savedGroupId) {
      const dashboardGroup = dashboardGroups[dashboardGroupId];
      const savedGroup = savedViewGroups[savedGroupId];

      const isParentInDashboard = dashboardGroup?.parentWidget === dashboardWidget.id;
      const wasParentInSaved = savedGroup?.parentWidget === savedWidget.id;

      if (isParentInDashboard !== wasParentInSaved) {
        return 'filter-overwrite';
      }
    }

    return 'none';
  }

  private findWidgetGroupForWidget(
    widgetId: string,
    widgetGroups: { [key: string]: WidgetGroup }
  ): string | undefined {
    for (const [groupId, group] of Object.entries(widgetGroups)) {
      if (group.widgets.includes(widgetId) || group.parentWidget === widgetId) {
        return groupId;
      }
    }
    return undefined;
  }

  private analyzeWidgetGroupChanges(
    dashboard: Dashboard,
    savedView: SavedView,
    result: SavedViewPropagationResult
  ): void {
    const dashboardGroupIds = new Set(Object.keys(dashboard.widgetGroups ?? {}));
    const savedViewGroupIds = new Set(Object.keys(savedView.widgetGroups ?? {}));

    for (const groupId of dashboardGroupIds) {
      if (!savedViewGroupIds.has(groupId)) {
        result.addedWidgetGroups.push(dashboard.widgetGroups[groupId]);
        result.hasChanges = true;
      }
    }

    for (const groupId of savedViewGroupIds) {
      if (!dashboardGroupIds.has(groupId)) {
        result.deletedWidgetGroupIds.push(groupId);
        result.hasChanges = true;
      }
    }
  }

  private analyzePageChanges(
    dashboard: Dashboard,
    savedView: SavedView,
    result: SavedViewPropagationResult
  ): void {
    const dashboardPageIds = new Set(dashboard.payload.pages.map((p) => p.id));

    const savedViewPageIds = new Set<number>();
    for (const widget of savedView.widgets ?? []) {
      if (widget.payload.page !== undefined) {
        savedViewPageIds.add(widget.payload.page);
      }
    }

    for (const pageId of dashboardPageIds) {
      if (!savedViewPageIds.has(pageId)) {
        result.addedPageIds.push(pageId);
        result.hasChanges = true;
      }
    }

    for (const pageId of savedViewPageIds) {
      if (!dashboardPageIds.has(pageId)) {
        result.deletedPageIds.push(pageId);
        result.hasChanges = true;
      }
    }
  }

  applyPropagation(
    dashboard: Dashboard,
    savedView: SavedView,
    propagationResult: SavedViewPropagationResult
  ): SavedView {
    if (!propagationResult.hasChanges) {
      return savedView;
    }

    const updatedWidgets: Widget[] = [];
    const updatedWidgetGroups: { [key: string]: WidgetGroup } = {};

    // Process existing widgets
    for (const savedWidget of savedView.widgets ?? []) {
      const changeType = propagationResult.widgetChanges.get(savedWidget.id);

      if (changeType === 'deleted') {
        continue;
      }

      const dashboardWidget = dashboard.widgets.find((w) => w.id === savedWidget.id);
      if (!dashboardWidget) {
        continue;
      }

      switch (changeType) {
        case 'none':
          updatedWidgets.push(savedWidget);
          break;
        case 'filter-overwrite':
          updatedWidgets.push(this.mergeWidgetFilterOverwrite(dashboardWidget, savedWidget));
          break;
        case 'chart-overwrite':
          updatedWidgets.push(this.mergeWidgetChartOverwrite(dashboardWidget, savedWidget));
          break;
        case 'full-overwrite':
          updatedWidgets.push(Utils.deepCloneWithNulls(dashboardWidget));
          break;
        default:
          updatedWidgets.push(savedWidget);
      }
    }

    for (const addedWidget of propagationResult.addedWidgets) {
      updatedWidgets.push(Utils.deepCloneWithNulls(addedWidget));
    }

    for (const [groupId, savedGroup] of Object.entries(savedView.widgetGroups ?? {})) {
      if (!propagationResult.deletedWidgetGroupIds.includes(groupId)) {
        const dashboardGroup = dashboard.widgetGroups?.[groupId];
        if (dashboardGroup) {
          updatedWidgetGroups[groupId] = this.mergeWidgetGroup(dashboardGroup, savedGroup);
        } else {
          updatedWidgetGroups[groupId] = savedGroup;
        }
      }
    }

    for (const addedGroup of propagationResult.addedWidgetGroups) {
      updatedWidgetGroups[addedGroup.id] = Utils.deepCloneWithNulls(addedGroup);
    }

    return {
      ...savedView,
      widgets: updatedWidgets,
      widgetGroups: updatedWidgetGroups,
    };
  }

  private mergeWidgetFilterOverwrite(dashboardWidget: Widget, savedWidget: Widget): Widget {
    const merged = Utils.deepCloneWithNulls(savedWidget);
    merged.payload.filters = Utils.deepCloneWithNulls(dashboardWidget.payload.filters);
    return merged;
  }

  private mergeWidgetChartOverwrite(dashboardWidget: Widget, savedWidget: Widget): Widget {
    const merged = Utils.deepCloneWithNulls(dashboardWidget);
    if (savedWidget.payload.filters) {
      merged.payload.filters = Utils.deepCloneWithNulls(savedWidget.payload.filters);
    }
    return merged;
  }

  private mergeWidgetGroup(dashboardGroup: WidgetGroup, savedGroup: WidgetGroup): WidgetGroup {
    const merged = Utils.deepCloneWithNulls(savedGroup);
    merged.widgets = [...dashboardGroup.widgets];
    merged.parentWidget = dashboardGroup.parentWidget;

    if (savedGroup.parentWidget && !dashboardGroup.parentWidget) {
      merged.parentFilters = {
        analyses: { and: [], or: [], not: [] },
        documents: { and: [], or: [], not: [] },
        metadata: { and: [], or: [], not: [] },
      };
    }

    return merged;
  }

  needsPropagation(
    dashboardRevision: number | undefined,
    savedViewDashboardRevision: number | undefined
  ): boolean {
    if (dashboardRevision === undefined) {
      return false;
    }
    if (savedViewDashboardRevision === undefined) {
      return true;
    }
    return dashboardRevision > savedViewDashboardRevision;
  }
}
